import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../auth/AuthContext.js';
import { usePrefs, APP_VERSION, THEMES, type Language } from '../lib/prefs.js';
import { api } from '../lib/api.js';

interface GitInfo { sha: string; subject: string; isoDate: string; branch: string }
interface Health { uptimeMs?: number; versions?: { node: string }; git?: GitInfo; ts: string }
interface Config { server?: { env?: string } }

type Tab = 'profile' | 'security' | 'system';

const LANGS: { id: Language; label: string }[] = [
  { id: 'fr', label: 'Français' }, { id: 'en', label: 'English' }, { id: 'es', label: 'Español' },
];

function fmtUptime(ms?: number): string {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function fmtCommitAge(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days}d ago` : d.toLocaleDateString();
}

export function ProfileCard({ open, onClose, onOpenSettings }: { open: boolean; onClose: () => void; onOpenSettings: () => void }) {
  const { user, logout } = useAuth();
  const [prefs, setPrefs] = usePrefs();
  const [tab, setTab] = useState<Tab>('profile');
  const [health, setHealth] = useState<Health | null>(null);
  const [env, setEnv] = useState<string>('—');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    api<Health>('/api/health').then(setHealth).catch(() => {});
    api<Config>('/api/admin/config').then((c) => setEnv(c.server?.env ?? '—')).catch(() => {});
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const initial = (user?.name ?? user?.email ?? '?').charAt(0).toUpperCase();

  const TABS: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'system', label: 'System' },
  ];

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-[380px] max-w-[92vw] max-h-[88vh] flex flex-col rounded-2xl border border-ink/15 bg-void/95 backdrop-blur-xl shadow-[0_40px_100px_rgba(0,0,0,0.6)] animate-[fadeIn_180ms_ease]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink/[0.07] shrink-0">
          <span className="font-serif text-lg text-ink">Profile &amp; Settings</span>
          <button onClick={onClose} className="w-7 h-7 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 shrink-0">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-1.5 rounded-lg text-[11.5px] transition-colors ${ tab === t.id ? 'bg-ink text-void' : 'text-ink/45 hover:text-ink/75' }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'profile' && (
            <>
              <Section label="Identity">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full grid place-items-center font-serif italic text-xl text-ink bg-gradient-to-br from-amber/60 to-amber/15">{initial}</div>
                  <div className="min-w-0">
                    <div className="font-serif text-[17px] text-ink leading-tight">{user?.name ?? '—'}</div>
                    <div className="font-mono text-[11px] text-ink/45 truncate">{user?.email}</div>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full border border-particle/30 text-particle text-[11px]">{user?.role}</span>
                  </div>
                </div>
              </Section>
              <Section label="Appearance">
                <div className="flex flex-wrap gap-1.5">
                  {THEMES.map((t) => {
                    const on = prefs.background === t.id;
                    return (
                      <button key={t.id} onClick={() => setPrefs({ background: t.id })} title={t.desc}
                        className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-colors ${on ? 'border-amber bg-amber/[0.08]' : 'border-ink/12 hover:border-ink/30'}`}>
                        <span className="w-5 h-5 rounded-full border border-ink/20" style={{ background: t.swatch }} />
                        <span className="font-serif text-[13px] text-ink">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-1.5 mt-3">
                  {LANGS.map((l) => (
                    <button key={l.id} onClick={() => setPrefs({ language: l.id })}
                      className={`flex-1 px-2 py-1.5 rounded-lg border text-[11.5px] ${prefs.language === l.id ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/50 hover:text-ink'}`}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </Section>
            </>
          )}

          {tab === 'security' && (
            <>
              <Section label="Change password"><ChangePassword /></Section>
              <Section label="Two-factor authentication"><TwoFactor email={user?.email ?? ''} /></Section>
            </>
          )}

          {tab === 'system' && (
            <>
              <Section label="Account">
                <Row k="Visibility tier" v={user?.tier?.replace('_', ' ') ?? '—'} />
                <Row k="Role" v={user?.role ?? '—'} />
                <Row k="User ID" v={user?.id?.slice(0, 8) ?? '—'} mono />
              </Section>
              <Section label="Build">
                <div className="font-mono text-[11px] text-ink/45 leading-relaxed">
                  {APP_VERSION} · {health?.git?.branch || 'main'}
                  {health?.git?.sha ? <> · <span className="text-ink/70">{health.git.sha}</span></> : null}
                  <br />
                  {health?.git?.subject ? (
                    <span className="text-ink/55" title={health.git.subject}>
                      {health.git.subject.length > 56 ? health.git.subject.slice(0, 55) + '…' : health.git.subject}
                    </span>
                  ) : <span>—</span>}
                  {health?.git?.isoDate ? <> · {fmtCommitAge(health.git.isoDate)}</> : null}
                  <br />
                  uptime {fmtUptime(health?.uptimeMs)} · node {health?.versions?.node ?? '—'}<br />
                  DB schema · {env}
                </div>
              </Section>
            </>
          )}
        </div>

        {/* Actions — always visible */}
        <div className="px-5 py-4 space-y-2 border-t border-ink/[0.07] shrink-0">
          <button onClick={() => { onClose(); onOpenSettings(); }}
            className="w-full py-2.5 rounded-xl border border-ink/15 text-[12px] text-ink/70 hover:text-ink hover:border-ink/30">
            All settings →
          </button>
          <button onClick={logout}
            className="w-full py-2.5 rounded-xl border border-red-400/30 text-[12px] text-red-300/80 hover:text-red-300 hover:border-red-400/50">
            ⏏ Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Change password ────────────────────────────────────────────────────────────
function ChangePassword() {
  const [cur, setCur] = useState(''); const [next, setNext] = useState(''); const [conf, setConf] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit() {
    setMsg(null);
    if (next.length < 6) return setMsg({ ok: false, text: 'New password must be ≥ 6 characters.' });
    if (next !== conf) return setMsg({ ok: false, text: 'Passwords do not match.' });
    setBusy(true);
    try {
      await api('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword: cur, newPassword: next }) });
      setMsg({ ok: true, text: 'Password updated ✓' }); setCur(''); setNext(''); setConf('');
    } catch (e) {
      const r = (e as { body?: { error?: string } })?.body?.error;
      setMsg({ ok: false, text: r === 'wrong_current_password' ? 'Current password is incorrect.' : 'Could not update password.' });
    } finally { setBusy(false); }
  }
  const inp = 'w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none';
  return (
    <div className="flex flex-col gap-2">
      <input type="password" placeholder="current password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" className={inp} />
      <input type="password" placeholder="new password (≥6)" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" className={inp} />
      <input type="password" placeholder="confirm new password" value={conf} onChange={(e) => setConf(e.target.value)} autoComplete="new-password" className={inp} />
      <button onClick={submit} disabled={busy || !cur || !next}
        className="self-start mt-1 px-4 py-1.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
        {busy ? 'Saving…' : 'Update password'}
      </button>
      {msg && <p className={`text-[11.5px] ${msg.ok ? 'text-emerald-300/80' : 'text-red-300/80'}`}>{msg.text}</p>}
    </div>
  );
}

// ── Two-factor (TOTP / Microsoft Authenticator) ─────────────────────────────────
function TwoFactor({ email }: { email: string }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauth: string; qr: string } | null>(null);
  const [code, setCode] = useState(''); const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [disarm, setDisarm] = useState(false);

  useEffect(() => { api<{ enabled: boolean }>('/api/auth/2fa/status').then((s) => setEnabled(s.enabled)).catch(() => setEnabled(false)); }, []);

  async function begin() {
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ secret: string; otpauth: string }>('/api/auth/2fa/setup', { method: 'POST' });
      const qr = await QRCode.toDataURL(r.otpauth, { margin: 1, width: 200, color: { dark: '#ffc24d', light: '#00000000' } });
      setSetup({ ...r, qr }); setCode('');
    } catch { setMsg('Could not start setup.'); } finally { setBusy(false); }
  }
  async function enable() {
    setBusy(true); setMsg(null);
    try {
      await api('/api/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
      setEnabled(true); setSetup(null); setCode('');
    } catch { setMsg('Invalid code — try the current one.'); } finally { setBusy(false); }
  }
  async function disable() {
    setBusy(true); setMsg(null);
    try {
      await api('/api/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
      setEnabled(false); setDisarm(false); setCode('');
    } catch { setMsg('Invalid code.'); } finally { setBusy(false); }
  }

  const codeInp = 'w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-center text-lg tracking-[0.3em] text-ink focus:border-ink/40 focus:outline-none';

  if (enabled === null) return <p className="text-[11.5px] text-ink/40">…</p>;

  // Enabled → show status + disable flow.
  if (enabled) {
    return (
      <div className="flex flex-col gap-2">
        <div className="font-mono text-[11px] text-emerald-300/85">● Enabled — a code is required at sign-in.</div>
        {!disarm ? (
          <button onClick={() => { setDisarm(true); setMsg(null); }} className="self-start px-4 py-1.5 rounded-full border border-red-400/30 text-[11.5px] text-red-300/80 hover:text-red-300">Disable 2FA</button>
        ) : (
          <>
            <p className="font-serif italic text-[12px] text-ink/55">Enter a current code from your authenticator to turn it off.</p>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="000000" className={codeInp} />
            <div className="flex gap-2">
              <button onClick={disable} disabled={busy || code.length !== 6} className="px-4 py-1.5 rounded-full bg-red-400 text-void text-[11.5px] disabled:opacity-30">Confirm disable</button>
              <button onClick={() => setDisarm(false)} className="px-3 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/55">Cancel</button>
            </div>
          </>
        )}
        {msg && <p className="text-[11.5px] text-red-300/80">{msg}</p>}
      </div>
    );
  }

  // Not enabled → setup flow.
  return (
    <div className="flex flex-col gap-2">
      {!setup ? (
        <>
          <p className="font-serif italic text-[12px] text-ink/55">Add a second step at sign-in with Microsoft / Google Authenticator.</p>
          <button onClick={begin} disabled={busy} className="self-start px-4 py-1.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
            {busy ? '…' : 'Set up 2FA'}
          </button>
        </>
      ) : (
        <>
          <p className="font-serif italic text-[12px] text-ink/55">Scan with your authenticator app, then enter the 6-digit code.</p>
          <div className="grid place-items-center py-1">
            <img src={setup.qr} alt="2FA QR code" className="w-44 h-44 rounded-lg" />
          </div>
          <div className="font-mono text-[10px] text-ink/40 break-all">Manual key: <span className="text-ink/70 select-all">{setup.secret}</span></div>
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="000000" autoFocus className={codeInp} />
          <div className="flex gap-2">
            <button onClick={enable} disabled={busy || code.length !== 6} className="px-4 py-1.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">{busy ? 'Verifying…' : 'Enable'}</button>
            <button onClick={() => { setSetup(null); setMsg(null); }} className="px-3 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/55">Cancel</button>
          </div>
          <span className="font-mono text-[8.5px] text-ink/30 break-all">{email}</span>
        </>
      )}
      {msg && <p className="text-[11.5px] text-red-300/80">{msg}</p>}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-5 py-4 border-b border-ink/[0.07]">
      <div className="text-[11px] text-ink/35 mb-3">{label}</div>
      {children}
    </div>
  );
}
function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[11.5px] text-ink/40">{k}</span>
      <span className={`text-[12px] text-ink/75 ${mono ? 'font-mono' : 'font-serif'}`}>{v}</span>
    </div>
  );
}
